import * as React from "react";

import { readMicrophoneId } from "@/lib/preferences";

/**
 * The single way into the microphone. Everything that records goes through here,
 * so the choice in the profile applies to dictation and live conversations alike.
 */
export async function requestMicrophone(
  audio: MediaTrackConstraints = {},
): Promise<MediaStream> {
  const deviceId = readMicrophoneId();
  if (!deviceId) return navigator.mediaDevices.getUserMedia({ audio });

  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: { ...audio, deviceId: { exact: deviceId } },
    });
  } catch (error) {
    if (!isDeviceGone(error)) throw error;
    // The preference survives on purpose: a headset that is unplugged today is
    // picked up again by itself once it comes back. Only this call falls back.
    return navigator.mediaDevices.getUserMedia({ audio });
  }
}

// `exact` and not `ideal`: an ideal constraint quietly hands back a different
// microphone, and someone who picked their headset would never learn that the
// laptop is recording instead.
function isDeviceGone(error: unknown): boolean {
  const name = (error as DOMException | null)?.name;
  return name === "OverconstrainedError" || name === "NotFoundError";
}

export type MicrophoneOption = {
  deviceId: string;
  label: string;
};

// Chrome lists the same hardware twice: once behind these aliases that follow
// the operating system, once with its own id. The aliases are dropped because
// "Systemstandard" already covers what they do.
const ALIAS_IDS = new Set(["default", "communications"]);

export type MicrophoneList = {
  microphones: MicrophoneOption[];
  /** The browser withholds device names until a recording was allowed once. */
  labelsHidden: boolean;
  supported: boolean;
  requestAccess: () => Promise<void>;
  refresh: () => Promise<void>;
};

export function useMicrophones(): MicrophoneList {
  const supported = typeof navigator !== "undefined" && !!navigator.mediaDevices;
  const [microphones, setMicrophones] = React.useState<MicrophoneOption[]>([]);
  const [labelsHidden, setLabelsHidden] = React.useState(false);

  const refresh = React.useCallback(async () => {
    if (!supported) return;
    let devices: MediaDeviceInfo[];
    try {
      devices = await navigator.mediaDevices.enumerateDevices();
    } catch {
      return;
    }
    const inputs = devices.filter(
      (device) =>
        device.kind === "audioinput" && !ALIAS_IDS.has(device.deviceId),
    );
    setLabelsHidden(inputs.length > 0 && inputs.every((device) => !device.label));
    setMicrophones(
      inputs.map((device, index) => ({
        deviceId: device.deviceId,
        label: device.label || `Mikrofon ${index + 1}`,
      })),
    );
  }, [supported]);

  React.useEffect(() => {
    void refresh();
    if (!supported) return;
    navigator.mediaDevices.addEventListener("devicechange", refresh);
    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", refresh);
    };
  }, [refresh, supported]);

  const requestAccess = React.useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
    } catch {
      // Denied or no device: refresh anyway, the list is then simply empty.
    }
    await refresh();
  }, [refresh]);

  return { microphones, labelsHidden, supported, requestAccess, refresh };
}
